import React, { useState } from 'react';
import { ColumnType } from 'antd/lib/table';
import {
  DraggableProvided,
  DraggableProvidedDragHandleProps,
  DraggableProvidedDraggableProps,
} from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';

import IbButton from '../../../../components/common/IbButton';
import IbContextMenu, { IIbContextMenuItem } from '../../../../components/common/IbContextMenu';
import IbDivider from '../../../../components/common/IbDivider';
import IbIcon from '../../../../components/common/IbIcon';
import IbSwitch from '../../../../components/common/IbSwitch';
import IbTable from '../../../../components/common/IbTable';
import IbTypography from '../../../../components/common/IbTypography';
import {
  getDetectorDisplayName,
  getDetectorSummaryItems,
  IMaskingDetectorStep,
  IGuardrailSummaryItem,
  isSettingsEnabled,
} from '../shared/utils';

const MAIN_CLASS_NAME = 'masking-detector-list-item';
const DISABLED_CLASS_NAME = `${MAIN_CLASS_NAME}_disabled`;
const DRAGGING_CLASS_NAME = `${MAIN_CLASS_NAME}_dragging`;
const HEADER_CLASS_NAME = `${MAIN_CLASS_NAME}__header`;
const ORDER_CLASS_NAME = `${HEADER_CLASS_NAME}__order`;
const DRAG_HANDLE_CLASS_NAME = `${HEADER_CLASS_NAME}__drag-handle`;
const DRAG_HANDLE_DISABLED_CLASS_NAME = `${DRAG_HANDLE_CLASS_NAME}_disabled`;
const DRAG_HANDLE_ICON_CLASS_NAME = `${DRAG_HANDLE_CLASS_NAME}__icon`;
const TITLE_CLASS_NAME = `${HEADER_CLASS_NAME}__title`;
const CONTEXT_CLASS_NAME = `${HEADER_CLASS_NAME}__context`;
const FOOTER_CLASS_NAME = `${MAIN_CLASS_NAME}__footer`;
const SUMMARY_CLASS_NAME = `${FOOTER_CLASS_NAME}__summary`;
const SUMMARY_TABLE_CLASS_NAME = `${SUMMARY_CLASS_NAME}__table`;
const SUMMARY_ITEM_CLASS_NAME = `${SUMMARY_CLASS_NAME}__item`;
const SUMMARY_ITEM_VALUE_CLASS_NAME = `${SUMMARY_ITEM_CLASS_NAME}__value`;

interface ISummaryTableRow {
  key: string;
}

export interface IDetectorListItemProps {
  index: number;
  total: number;
  saving: boolean;
  step: IMaskingDetectorStep;
  draggableProps?: DraggableProvidedDraggableProps;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  innerRef?: DraggableProvided['innerRef'];
  isDragging?: boolean;
  onDelete: (stepKey: string) => void;
  onMove: (stepKey: string, direction: -1 | 1) => void;
  onSetup: (stepKey: string) => void;
  onStateChange: (stepKey: string, enabled: boolean) => void;
}

const DetectorListItem: React.FC<IDetectorListItemProps> = ({
  index,
  total,
  saving,
  step,
  draggableProps,
  dragHandleProps,
  innerRef,
  isDragging,
  onDelete,
  onMove,
  onSetup,
  onStateChange,
}) => {
  const { t } = useTranslation();
  const [contextVisible, setContextVisible] = useState(false);
  const enabled = isSettingsEnabled(step.detector.state);
  const summaryItems = getDetectorSummaryItems(step);
  const className = [MAIN_CLASS_NAME, !enabled ? DISABLED_CLASS_NAME : '', isDragging ? DRAGGING_CLASS_NAME : '']
    .filter(Boolean)
    .join(' ');

  const onSetupSelect = () => {
    onSetup(step.key);
  };

  const onMoveUpSelect = () => {
    onMove(step.key, -1);
  };

  const onMoveDownSelect = () => {
    onMove(step.key, 1);
  };

  const onDeleteSelect = () => {
    onDelete(step.key);
  };

  const onStateChangeChange = (checked: boolean) => {
    onStateChange(step.key, checked);
  };

  const onContextClose = () => {
    setContextVisible(false);
  };

  const onContextOpen = () => {
    setContextVisible(true);
  };

  const menuItems = [
    {
      icon: <IbIcon iconName="setting-two" size={20} />,
      text: t('Agent: Guardrails: Steps: Setup'),
      disabled: saving,
      onSelect: onSetupSelect,
    },
    {
      icon: <IbIcon iconName="up" size={20} />,
      text: t('Agent: Guardrails: Steps: Move up'),
      disabled: saving || index === 0,
      onSelect: onMoveUpSelect,
    },
    {
      icon: <IbIcon iconName="down" size={20} />,
      text: t('Agent: Guardrails: Steps: Move down'),
      disabled: saving || index === total - 1,
      onSelect: onMoveDownSelect,
    },
    {
      icon: <IbIcon iconName="delete" size={20} />,
      text: t('Agent: Guardrails: Steps: Delete'),
      disabled: saving,
      onSelect: onDeleteSelect,
    },
  ] as IIbContextMenuItem[];

  const renderSummaryValue = (summaryItem: IGuardrailSummaryItem) => (
    <div className={SUMMARY_ITEM_VALUE_CLASS_NAME}>
      <IbTypography.Paragraph type="secondary">{summaryItem.value}</IbTypography.Paragraph>
    </div>
  );

  const summaryColumns: ColumnType<ISummaryTableRow>[] = summaryItems
    .filter((summaryItem) => summaryItem.kind !== 'prompt')
    .map((summaryItem, summaryIndex) => ({
      title: summaryItem.label,
      render: () => renderSummaryValue(summaryItem),
      width: summaryIndex === 0 ? '20%' : undefined,
    }));

  const summaryDataSource: ISummaryTableRow[] = [{ key: step.key }];

  const dragHandleClassName = [DRAG_HANDLE_CLASS_NAME, !dragHandleProps ? DRAG_HANDLE_DISABLED_CLASS_NAME : '']
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={innerRef} className={className} {...draggableProps}>
      <div className={HEADER_CLASS_NAME}>
        <div className={ORDER_CLASS_NAME}>{index + 1}</div>
        <div className={dragHandleClassName} {...(dragHandleProps ?? {})}>
          <IbIcon className={DRAG_HANDLE_ICON_CLASS_NAME} iconName="drag" size={16} />
        </div>
        <IbSwitch checked={enabled} disabled={saving} size="small" onChange={onStateChangeChange} />
        <div className={TITLE_CLASS_NAME}>
          <IbTypography.Paragraph ellipsis strong type="secondary">
            {getDetectorDisplayName(step.detector)}
          </IbTypography.Paragraph>
        </div>
        <IbContextMenu menuItems={menuItems} visible={contextVisible} onClose={onContextClose}>
          <IbButton
            className={CONTEXT_CLASS_NAME}
            icon={<IbIcon iconName="more-one" />}
            type="icon"
            onClick={onContextOpen}
          />
        </IbContextMenu>
      </div>
      <IbDivider />
      <div className={FOOTER_CLASS_NAME}>
        <div className={SUMMARY_CLASS_NAME}>
          <IbTable
            className={SUMMARY_TABLE_CLASS_NAME}
            columns={summaryColumns}
            dataSource={summaryDataSource}
            rowKey="key"
            size="small"
          />
        </div>
      </div>
    </div>
  );
};

export default DetectorListItem;
